/**
 * TrackingIdentityCard — P0.17 SPRINT (Aug 2026)
 *
 * Toont de UNIFIED SHIPMENT IDENTITY van één canonical shipment:
 *   • Canonical W-nummer (5999Cargo SaaS)
 *   • Legacy Tracking (oorspronkelijk ManagerCargo nummer)
 *   • Aliassen (extra gekoppelde tracking-nummers)
 *   • Externe koppeling (linked_external_tracking_number)
 *   • External IDs (generieke 3PL/warehouse codes)
 *   • Laatste synchronisatie met upstream ManagerCargo
 *
 * Acties (Owner/Admin/Manager):
 *   • + Alias toevoegen
 *   • Alias verwijderen (X per rij)
 *   • Synchroniseer ManagerCargo
 *
 * Medewerker heeft alleen leesrechten (knoppen worden verborgen).
 *
 * DoD referentie:
 *   ✓ Eén shipment = één dossier — knoppen updaten alleen deze shipment
 *   ✓ RBAC — medewerker read-only
 *   ✓ Failover — sync-endpoint toont per-nummer status incl. "ManagerCargo
 *     tijdelijk niet bereikbaar" bij upstream errors
 */
import React, { useState } from "react";
import { toast } from "sonner";
import {
  Fingerprint, Plus, X, RefreshCw, Loader2, Copy, CheckCircle2,
  AlertCircle, Radio,
} from "lucide-react";

import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { adminApi, useAuth, formatApiError } from "../../../context/AuthContext";

const PROVIDER_LABELS = {
  managercargo: "ManagerCargo",
  external: "Extern systeem",
  saas: "5999Cargo SaaS",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("nl-NL", {
      dateStyle: "short", timeStyle: "short",
    });
  } catch { return iso; }
}

export default function TrackingIdentityCard({ label, onChanged }) {
  const { hasPermission, user } = useAuth();
  const isMedewerker = (user?.role || "").toLowerCase() === "medewerker";
  const canEdit = hasPermission?.("quotes:edit") && !isMedewerker;

  const [aliasInput, setAliasInput] = useState("");
  const [providerInput, setProviderInput] = useState("managercargo");
  const [adding, setAdding] = useState(false);
  const [removingAlias, setRemovingAlias] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);

  const canonical = label.tracking_number || "—";
  const legacy = label.legacy_tracking_number;
  const legacyProvider = label.legacy_provider;
  const aliases = Array.isArray(label.tracking_aliases) ? label.tracking_aliases : [];
  const externalIds = Array.isArray(label.external_ids) ? label.external_ids : [];
  const linkedExternal = label.linked_external_tracking_number;
  const lastSync = label.external_sync_at;
  const syncSummary = label.external_sync_summary || null;

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${text} gekopieerd`);
    } catch {
      toast.error("Kopiëren mislukt");
    }
  };

  const handleAddAlias = async () => {
    const raw = (aliasInput || "").trim().toUpperCase();
    if (!raw) {
      toast.error("Vul een tracking-nummer in");
      return;
    }
    setAdding(true);
    try {
      const { data: updated } = await adminApi.post(
        `/admin/shipping-labels/${label.id}/tracking-aliases`,
        {
          alias: raw,
          provider: providerInput || undefined,
        }
      );
      toast.success(`Alias ${raw} toegevoegd`);
      setAliasInput("");
      setShowAddForm(false);
      onChanged?.(updated);
    } catch (err) {
      toast.error(formatApiError(err) || "Alias toevoegen mislukt");
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveAlias = async (alias) => {
    if (!window.confirm(`Alias ${alias} loskoppelen van deze zending?`)) return;
    setRemovingAlias(alias);
    try {
      const { data: updated } = await adminApi.delete(
        `/admin/shipping-labels/${label.id}/tracking-aliases/${encodeURIComponent(alias)}`
      );
      toast.success(`Alias ${alias} verwijderd`);
      onChanged?.(updated);
    } catch (err) {
      toast.error(formatApiError(err) || "Alias verwijderen mislukt");
    } finally {
      setRemovingAlias(null);
    }
  };

  const handleSyncExternal = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const { data: result } = await adminApi.post(
        `/admin/shipping-labels/${label.id}/sync-external`
      );
      setSyncResult(result);
      if (result.status === "no_external_numbers") {
        toast.info(result.message || "Geen externe nummers om te synchroniseren");
      } else {
        const ok = (result.results || []).filter((r) => r.status === "ok").length;
        const err = (result.results || []).filter((r) => r.status === "error").length;
        if (err === 0) {
          toast.success(`${result.total_events || 0} events opgehaald van ManagerCargo`);
        } else if (ok === 0) {
          toast.error("ManagerCargo tijdelijk niet bereikbaar");
        } else {
          toast.warning(`${ok} succesvol, ${err} mislukt`);
        }
      }
      // Refresh label
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err) || "Synchronisatie mislukt");
    } finally {
      setSyncing(false);
    }
  };

  const anyExternal = !!(legacy || linkedExternal || aliases.length || externalIds.length);

  return (
    <div
      className="bg-white border border-gray-200 p-4"
      data-testid="tracking-identity-card"
    >
      <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <Fingerprint className="w-4 h-4 text-[#0055FF]" />
          <h3 className="text-[11px] uppercase tracking-wider font-bold text-[#0A192F]">
            Tracking Identiteit
          </h3>
        </div>
        {canEdit && anyExternal && (
          <Button
            size="sm"
            variant="outline"
            onClick={handleSyncExternal}
            disabled={syncing}
            className="rounded-none h-7 text-[11px]"
            data-testid="tracking-identity-sync-btn"
          >
            {syncing
              ? <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />
              : <RefreshCw className="w-3 h-3 mr-1.5" />}
            Synchroniseer ManagerCargo
          </Button>
        )}
      </div>

      {/* Canonical W-nummer */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
        <div>
          <div className="text-[10px] uppercase font-bold text-gray-500 tracking-wide mb-1">
            Canonical Tracking (SaaS)
          </div>
          <div className="flex items-center gap-2">
            <div className="font-mono font-bold text-sm text-[#0A192F]" data-testid="tracking-identity-canonical">
              {canonical}
            </div>
            <button
              onClick={() => copyToClipboard(canonical)}
              className="p-1 hover:bg-gray-100 rounded"
              data-testid="tracking-identity-copy-canonical"
            >
              <Copy className="w-3 h-3 text-gray-400" />
            </button>
          </div>
        </div>
        {legacy && (
          <div>
            <div className="text-[10px] uppercase font-bold text-gray-500 tracking-wide mb-1">
              Legacy Tracking
              {legacyProvider && (
                <span className="ml-1 text-[9px] text-gray-400">
                  · {PROVIDER_LABELS[legacyProvider] || legacyProvider}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <div className="font-mono text-sm text-[#0A192F]" data-testid="tracking-identity-legacy">
                {legacy}
              </div>
              <button
                onClick={() => copyToClipboard(legacy)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <Copy className="w-3 h-3 text-gray-400" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* External link (backwards compat) */}
      {linkedExternal && (
        <div className="mb-3">
          <div className="text-[10px] uppercase font-bold text-gray-500 tracking-wide mb-1">
            Externe koppeling
          </div>
          <div className="flex items-center gap-2">
            <div className="font-mono text-sm text-[#0A192F]">
              {linkedExternal}
            </div>
            <button
              onClick={() => copyToClipboard(linkedExternal)}
              className="p-1 hover:bg-gray-100 rounded"
            >
              <Copy className="w-3 h-3 text-gray-400" />
            </button>
          </div>
        </div>
      )}

      {/* Aliases lijst */}
      <div className="mb-3">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase font-bold text-gray-500 tracking-wide">
            Aliassen ({aliases.length})
          </div>
          {canEdit && !showAddForm && (
            <button
              onClick={() => setShowAddForm(true)}
              className="flex items-center gap-1 text-[10px] text-[#0055FF] hover:underline uppercase font-bold tracking-wide"
              data-testid="tracking-identity-add-alias-btn"
            >
              <Plus className="w-3 h-3" /> Alias toevoegen
            </button>
          )}
        </div>
        {aliases.length === 0 ? (
          <div className="text-xs text-gray-400 italic">
            Geen extra tracking-nummers gekoppeld.
          </div>
        ) : (
          <div className="space-y-1">
            {aliases.map((alias) => (
              <div
                key={alias}
                className="flex items-center justify-between bg-gray-50 border border-gray-100 px-2.5 py-1.5"
                data-testid={`tracking-identity-alias-${alias}`}
              >
                <div className="font-mono text-xs text-[#0A192F]">{alias}</div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => copyToClipboard(alias)}
                    className="p-1 hover:bg-white rounded"
                  >
                    <Copy className="w-3 h-3 text-gray-400" />
                  </button>
                  {canEdit && (
                    <button
                      onClick={() => handleRemoveAlias(alias)}
                      disabled={removingAlias === alias}
                      className="p-1 hover:bg-red-50 rounded"
                      data-testid={`tracking-identity-remove-alias-${alias}`}
                    >
                      {removingAlias === alias
                        ? <Loader2 className="w-3 h-3 animate-spin text-red-500" />
                        : <X className="w-3 h-3 text-red-500" />}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add-form */}
      {showAddForm && canEdit && (
        <div className="mb-3 p-3 border border-[#0055FF]/30 bg-[#0055FF]/5">
          <div className="text-[10px] uppercase font-bold text-[#0055FF] tracking-wide mb-2">
            Nieuwe alias toevoegen
          </div>
          <div className="flex flex-col md:flex-row gap-2">
            <Input
              value={aliasInput}
              onChange={(e) => setAliasInput(e.target.value.toUpperCase())}
              placeholder="Bijv. W523518422761"
              className="rounded-none font-mono text-sm"
              data-testid="tracking-identity-alias-input"
            />
            <select
              value={providerInput}
              onChange={(e) => setProviderInput(e.target.value)}
              className="rounded-none border border-gray-300 px-2 text-sm"
              data-testid="tracking-identity-provider-select"
            >
              <option value="managercargo">ManagerCargo</option>
              <option value="external">Extern systeem</option>
            </select>
            <Button
              size="sm"
              onClick={handleAddAlias}
              disabled={adding || !aliasInput.trim()}
              className="rounded-none bg-[#0055FF] text-white hover:bg-[#0044CC] text-xs"
              data-testid="tracking-identity-alias-submit"
            >
              {adding && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
              Toevoegen
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => { setShowAddForm(false); setAliasInput(""); }}
              className="rounded-none text-xs"
            >
              Annuleren
            </Button>
          </div>
          <div className="mt-1.5 text-[10px] text-gray-500">
            Eén nummer per zending — ManagerCargo-events worden automatisch samengevoegd.
          </div>
        </div>
      )}

      {/* External IDs (advanced) */}
      {externalIds.length > 0 && (
        <div className="mb-3">
          <div className="text-[10px] uppercase font-bold text-gray-500 tracking-wide mb-1">
            External IDs ({externalIds.length})
          </div>
          <div className="flex flex-wrap gap-1">
            {externalIds.map((eid) => (
              <span
                key={eid}
                className="font-mono text-[10px] bg-gray-50 border border-gray-200 px-1.5 py-0.5"
              >
                {eid}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Sync footer */}
      {(lastSync || syncResult) && (
        <div className="mt-3 pt-2 border-t border-gray-100 text-[10px] text-gray-500">
          <div className="flex items-center gap-1.5">
            <Radio className="w-3 h-3" />
            <span>
              Laatste synchronisatie: <span className="font-mono">{fmtDate(lastSync)}</span>
              {syncSummary && (
                <> · {syncSummary.total_events || 0} events · {syncSummary.success_count}/{syncSummary.total_numbers} bronnen ok</>
              )}
            </span>
          </div>
          {syncResult && Array.isArray(syncResult.results) && (
            <div className="mt-1 space-y-0.5">
              {syncResult.results.map((r) => (
                <div key={r.tracking_number} className="flex items-center gap-1.5 pl-4">
                  {r.status === "ok"
                    ? <CheckCircle2 className="w-3 h-3 text-green-500" />
                    : <AlertCircle className="w-3 h-3 text-amber-500" />}
                  <span className="font-mono">{r.tracking_number}</span>
                  <span>—</span>
                  <span>
                    {r.status === "ok"
                      ? `${r.events_fetched || 0} events${r.status_label ? ` · ${r.status_label}` : ""}`
                      : r.message}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {!canEdit && anyExternal && (
        <div className="mt-2 text-[10px] text-gray-400 italic">
          Alleen lezen — Manager+ mag aliassen beheren en synchronisatie starten.
        </div>
      )}
    </div>
  );
}
